/**
 * Maps backend form keys ↔ Application Form Library card ids,
 * and which HR Documents tab holds each form.
 *
 * Shared by Documents edit navigation and the Applications library
 * (filled status + "Go to Forms").
 */

/** Backend formKey → frontend library card id */
export const BACKEND_FORM_KEY_TO_LIBRARY_ID = {
  employment_application: "application-form",
  reference_background_check: "reference-request",
  welcome_letter: "welcome-letter",
  employment_agreement: "employment-agreement",
  state_mandate_compliance: "state-mandate",
  job_description: "job-description",
  skill_inventory: "skill-inventory",
  post_test: "post-test",
  administrative_orientation: "administrative-orientation",
  orientation_checklist: "orientation-checklist",
  handbook_form: "handbook",
  identification_card: "identification-card",
  hepatitis_form: "hepatitis",
  confidentiality_agreement: "confidentiality",
  competency: "competency",
  w_4: "w4",
  w_9: "w9",
  i_9: "i9",
  // Canonical tax keys (forms-common FORM_KEYS + TaxAndPayrollType enum)
  edd: "de4",
  direct_deposit: "direct-deposit-guide",
  // Legacy aliases — keep so older payloads still resolve
  de_4: "de4",
  direct_deposit_guide: "direct-deposit-guide",
  chha_competency_test: "chha-post-test",
  evaluation: "evaluation",
  tb_test_form: "tb-test",
  flu_vaccination_program: "flu-vaccination",
  employee_health_examination: "employee-health-examination",
  contractor_agreement: "contractor-agreement",
  contractor_form: "contractor-agreement",
  contractor: "contractor-agreement",
  role_and_responsibility: "role-and-responsibility",
  roles_and_responsibilities: "role-and-responsibility",
};

/** Library card id → HR Documents tab */
export const LIBRARY_FORM_DOCUMENTS_TAB = {
  "application-form": "application-forms",
  "reference-request": "application-forms",
  "welcome-letter": "application-forms",
  "employment-agreement": "application-forms",
  "state-mandate": "application-forms",
  "job-description": "application-forms",
  "skill-inventory": "application-forms",
  "post-test": "application-forms",
  "administrative-orientation": "application-forms",
  "orientation-checklist": "application-forms",
  handbook: "application-forms",
  "identification-card": "application-forms",
  hepatitis: "application-forms",
  confidentiality: "application-forms",
  competency: "application-forms",
  "role-and-responsibility": "application-forms",
  "chha-post-test": "application-forms",
  "contractor-agreement": "application-forms",
  "tb-test": "medical-forms",
  "flu-vaccination": "medical-forms",
  "employee-health-examination": "medical-forms",
  w4: "tax-forms",
  w9: "tax-forms",
  i9: "tax-forms",
  de4: "tax-forms",
  "direct-deposit-guide": "tax-forms",
};

/** Forms that may have many saved copies per employee (library + Documents). */
export const LIBRARY_FORMS_ALLOW_MULTIPLE = new Set([
  "competency",
  "chha-post-test",
]);

/** Forms that are view-only — never show “already filled” gate. */
export const LIBRARY_FORMS_SKIP_FILL_GATE = new Set([
  "employee-handbook-readonly",
  "caregiver-employee-handbook-readonly",
]);

/**
 * Collapse expanded documents-list rows into libraryId → filled|pending.
 * @param {Array<{ formKey?: string, status?: string }>} rows
 * @returns {Record<string, 'filled' | 'pending'>}
 */
export function buildLibraryFillStatusMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const formKey = row?.formKey;
    if (!formKey) return;
    const libraryId = BACKEND_FORM_KEY_TO_LIBRARY_ID[formKey];
    if (!libraryId) return;

    const raw = String(
      row.status || row.detail?.status || "pending",
    ).toLowerCase();
    // Backend fill API uses "filled"; tax row workflow statuses also mean submitted.
    const status =
      raw === "filled" ||
      raw === "send_to_office" ||
      raw === "completed" ||
      raw === "to_be_corrected"
        ? "filled"
        : "pending";

    // Once filled, keep filled even if another expanded row is pending.
    if (map[libraryId] === "filled") return;
    map[libraryId] = status;
  });
  return map;
}

/** libraryId → first saved record id (for edit / update on reopen). */
export function buildLibraryRecordIdMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const formKey = row?.formKey;
    if (!formKey) return;
    const libraryId = BACKEND_FORM_KEY_TO_LIBRARY_ID[formKey];
    if (!libraryId || map[libraryId]) return;

    const details = row?.details;
    if (Array.isArray(details) && details.length > 0 && details[0]?.id) {
      map[libraryId] = details[0].id;
      return;
    }
    if (row?.detail?.id) {
      map[libraryId] = row.detail.id;
    }
  });
  return map;
}

export function getDocumentsTabForLibraryForm(formId) {
  return LIBRARY_FORM_DOCUMENTS_TAB[formId] || "application-forms";
}
